import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ApiError } from "@anyseal/api-client";
import {
  Button as AntButton,
  Card,
  Empty,
  Input,
  Select,
  Table,
  Tag,
  Tooltip,
  Typography,
  type TableColumnsType,
} from "antd";
import { Check, Copy, Search } from "lucide-react";
import {
  Button,
  CodeBlock,
  PageHeader,
  StatePanel,
} from "@anyseal/ui";
import { portalClient } from "../api";
import { useI18n } from "../i18n";
import { vendorColor } from "../vendorColors";
import { type DirectoryItem } from "./directory";
import { CAP_META, formatContext, formatPrice } from "./modelCatalog";
import { VendorAvatar } from "./VendorAvatar";

/** cURL quick-start for a model. The domain is a user-visible example
 * placeholder, so it uses the lonmax spelling (AGENTS.md identity). */
function sampleCurl(modelName: string): string {
  return `curl https://api.lonmaxcloud.com/v1/chat/completions \\
  -H "Authorization: Bearer $LONMAX_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "${modelName}",
    "messages": [{"role": "user", "content": "Hello"}]
  }'`;
}

export function ModelsPage() {
  const { locale } = useI18n();
  const isChinese = locale === "zh-CN";
  const [search, setSearch] = useState("");
  const [vendor, setVendor] = useState("");
  const [protocol, setProtocol] = useState("");
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [copiedKey, setCopiedKey] = useState("");

  const query = useQuery({
    queryKey: ["portal", "model-directory"],
    queryFn: () => portalClient.get<{ items: DirectoryItem[] }>("/api/portal/v1/models/directory"),
  });

  const items = useMemo(() => query.data?.items ?? [], [query.data]);

  const vendors = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of items) {
      if (!item.vendor) continue;
      counts.set(item.vendor, (counts.get(item.vendor) ?? 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([name, count]) => ({ name, count }));
  }, [items]);

  const protocols = useMemo(
    () => [...new Set(items.flatMap((item) => item.protocols))].sort(),
    [items],
  );

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return items.filter((item) => {
      const matchesSearch =
        !needle ||
        `${item.model_name} ${item.display_name} ${item.vendor}`.toLowerCase().includes(needle);
      const matchesVendor = !vendor || item.vendor === vendor;
      const matchesProtocol = !protocol || item.protocols.includes(protocol);
      return matchesSearch && matchesVendor && matchesProtocol;
    });
  }, [items, search, vendor, protocol]);

  if (query.isLoading) {
    return (
      <StatePanel
        kind="loading"
        title={isChinese ? "正在加载模型目录" : "Loading model directory"}
      />
    );
  }

  if (query.error) {
    return (
      <StatePanel
        kind="error"
        title={isChinese ? "模型目录暂时不可用" : "Model directory unavailable"}
        description={query.error.message}
        requestId={query.error instanceof ApiError ? query.error.requestId : undefined}
        action={
          <Button variant="secondary" onClick={() => void query.refetch()}>
            {isChinese ? "重试" : "Retry"}
          </Button>
        }
      />
    );
  }

  const copySample = (item: DirectoryItem) => {
    void navigator.clipboard.writeText(sampleCurl(item.model_name));
    setCopiedKey(item.model_name);
    window.setTimeout(() => setCopiedKey(""), 1600);
  };

  const columns: TableColumnsType<DirectoryItem> = [
    {
      title: isChinese ? "模型" : "Model",
      key: "model",
      sorter: (a, b) => a.display_name.localeCompare(b.display_name),
      render: (_, row) => (
        <div className="portal-model-cell">
          <VendorAvatar vendor={row.vendor} />
          <div className="portal-model-cell__name">
            <Typography.Text strong>{row.display_name || row.model_name}</Typography.Text>
            <span className="portal-model-cell__id">{row.model_name}</span>
          </div>
        </div>
      ),
    },
    {
      title: isChinese ? "厂商" : "Vendor",
      key: "vendor",
      width: 130,
      sorter: (a, b) => a.vendor.localeCompare(b.vendor),
      render: (_, row) => (
        <span className="portal-vendor-cell">
          <span
            className="portal-vendor-dot"
            style={{ background: row.vendor ? vendorColor(row.vendor) : "var(--as-muted)" }}
            aria-hidden="true"
          />
          {row.vendor || (isChinese ? "多供应商" : "Multi-vendor")}
        </span>
      ),
    },
    {
      title: isChinese ? "上下文" : "Context",
      key: "context",
      width: 92,
      align: "right",
      render: (_, row) => (
        <Tooltip title={isChinese ? "模型上下文窗口长度(tokens)" : "Model context window (tokens)"}>
          <span className={`portal-table-num${row.context_tokens ? "" : " portal-table-num--empty"}`}>
            {formatContext(row.context_tokens)}
          </span>
        </Tooltip>
      ),
    },
    {
      title: isChinese ? "输入 /1M" : "Input /1M",
      key: "input",
      width: 100,
      align: "right",
      render: (_, row) => (
        <Tooltip title={isChinese ? "每 100 万输入 tokens · 多通道最低价" : "per 1M input tokens · lowest across providers"}>
          <span className={`portal-table-num${row.input_price != null ? "" : " portal-table-num--empty"}`}>
            {formatPrice(row.input_price)}
          </span>
        </Tooltip>
      ),
    },
    {
      title: isChinese ? "输出 /1M" : "Output /1M",
      key: "output",
      width: 100,
      align: "right",
      render: (_, row) => (
        <Tooltip title={isChinese ? "每 100 万输出 tokens · 多通道最低价" : "per 1M output tokens · lowest across providers"}>
          <span className={`portal-table-num${row.output_price != null ? "" : " portal-table-num--empty"}`}>
            {formatPrice(row.output_price)}
          </span>
        </Tooltip>
      ),
    },
    {
      title: isChinese ? "能力" : "Capabilities",
      key: "capabilities",
      width: 200,
      render: (_, row) => {
        if (row.capabilities.length === 0) {
          return <span className="portal-table-num portal-table-num--empty">—</span>;
        }
        const shown = row.capabilities.slice(0, 4);
        const rest = row.capabilities.length - shown.length;
        return (
          <div className="portal-caps">
            {shown.map((cap) => {
              const meta = CAP_META[cap.toLowerCase()];
              return meta ? (
                <Tooltip key={cap} title={meta.hint}>
                  <span className="portal-cap">
                    {meta.icon}
                    <span>{isChinese ? meta.zh : meta.en}</span>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip key={cap} title={cap}>
                  <span className="portal-cap portal-cap--raw">{cap}</span>
                </Tooltip>
              );
            })}
            {rest > 0 && (
              <Tooltip title={row.capabilities.slice(4).join(", ")}>
                <span className="portal-cap portal-cap--more">+{rest}</span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: isChinese ? "协议" : "Protocols",
      key: "protocols",
      width: 140,
      render: (_, row) =>
        row.protocols.length === 0 ? (
          <span className="portal-table-num portal-table-num--empty">—</span>
        ) : (
          <div className="portal-protocol-tags">
            {row.protocols.map((value) => (
              <Tag key={value}>{value}</Tag>
            ))}
          </div>
        ),
    },
  ];

  const emptyCatalog = items.length === 0;

  return (
    <div className="portal-page-grid">
      <PageHeader
        eyebrow={isChinese ? "工作区" : "Workspace"}
        title={isChinese ? "模型目录" : "Model directory"}
        description={
          isChinese
            ? "当前项目可调用的模型、能力、协议与价格。目录来自动态发现并经过 LonMax AI 元数据校正；点击任意一行查看接入示例。"
            : "Models available to this project with capabilities, protocols, and pricing. The directory combines live discovery with reviewed metadata. Click any row for a request example."
        }
      />

      {/* ── Filter bar ── */}
      <Card className="portal-models-filters" variant="outlined">
        <Input
          className="portal-models-filters__search"
          prefix={<Search size={15} />}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={isChinese ? "搜索模型或厂商" : "Search models or vendors"}
          allowClear
        />
        <Select
          className="portal-models-filters__select"
          value={vendor || undefined}
          onChange={(value) => setVendor(value ?? "")}
          allowClear
          placeholder={isChinese ? "全部厂商" : "All vendors"}
          options={vendors.map((entry) => ({
            value: entry.name,
            label: `${entry.name} (${entry.count})`,
          }))}
        />
        <Select
          className="portal-models-filters__select"
          value={protocol || undefined}
          onChange={(value) => setProtocol(value ?? "")}
          allowClear
          placeholder={isChinese ? "全部协议" : "All protocols"}
          options={protocols.map((value) => ({ value, label: value }))}
        />
      </Card>

      {/* ── Model table ── */}
      <Card
        className="portal-section-card portal-models-table"
        variant="outlined"
        title={isChinese ? `全部模型 · ${filtered.length}` : `All models · ${filtered.length}`}
      >
        {emptyCatalog ? (
          <StatePanel
            kind="empty"
            title={isChinese ? "模型目录暂为空" : "Model directory is empty"}
            description={
              isChinese
                ? "通道配置完成后,可用模型会自动出现在这里。"
                : "Models appear here automatically once channels are configured."
            }
          />
        ) : (
          <Table<DirectoryItem>
            rowKey="model_name"
            dataSource={filtered}
            columns={columns}
            size="middle"
            scroll={{ x: 1080 }}
            expandedRowKeys={expandedKeys}
            onExpandedRowsChange={(keys) => setExpandedKeys(keys as string[])}
            onRow={(record) => ({
              onClick: () =>
                setExpandedKeys((prev) =>
                  prev.includes(record.model_name)
                    ? prev.filter((key) => key !== record.model_name)
                    : [...prev, record.model_name],
                ),
            })}
            expandable={{
              expandIcon: ({ expanded, onExpand, record }) => (
                <AntButton
                  type="text"
                  size="small"
                  className={`portal-models-table__expand${expanded ? " is-expanded" : ""}`}
                  aria-label={
                    expanded
                      ? `${isChinese ? "收起" : "Collapse"} ${record.display_name}`
                      : `${isChinese ? "展开接入示例" : "Expand request example"} ${record.display_name}`
                  }
                  aria-expanded={expanded}
                  onClick={(event) => {
                    event.stopPropagation();
                    onExpand(record, event);
                  }}
                  icon={<span className="portal-models-table__chevron">›</span>}
                />
              ),
              expandedRowRender: (record) => (
                <div className="portal-model-example">
                  <div className="portal-model-example__head">
                    <span className="portal-model-example__title">
                      {isChinese ? `${record.display_name || record.model_name} 接入示例` : `Request example · ${record.display_name || record.model_name}`}
                    </span>
                    <Typography.Text className="portal-model-example__hint" type="secondary">
                      {isChinese
                        ? "将 $LONMAX_API_KEY 替换为项目密钥;请求头、密钥与响应不会被平台记录。"
                        : "Replace $LONMAX_API_KEY with a project key; headers, keys, and bodies are never retained."}
                    </Typography.Text>
                  </div>
                  <CodeBlock
                    label="cURL"
                    action={
                      <Button
                        variant="quiet"
                        size="small"
                        onClick={() => copySample(record)}
                      >
                        {copiedKey === record.model_name ? <Check size={14} /> : <Copy size={14} />}
                        {copiedKey === record.model_name
                          ? isChinese ? "已复制" : "Copied"
                          : isChinese ? "复制" : "Copy"}
                      </Button>
                    }
                  >
                    {sampleCurl(record.model_name)}
                  </CodeBlock>
                  <div className="portal-model-example__foot">
                    <Link to="/dashboard/docs" className="portal-inline-link">
                      {isChinese ? "查看完整开发文档" : "Open the developer docs"} ↗
                    </Link>
                  </div>
                </div>
              ),
            }}
            pagination={{ pageSize: 20, showSizeChanger: false, hideOnSinglePage: true }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={isChinese ? "没有匹配的模型" : "No matching models"}
                />
              ),
            }}
          />
        )}
      </Card>
    </div>
  );
}
